import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaPlay,
  FaTimes,
} from "react-icons/fa";
import { getPublicBlogPosts, getUpgradePlans } from "../api";
import PublicAccountMenu from "../components/PublicAccountMenu";
import SEO from "../components/SEO";
import { useAuth } from "../contexts/AuthContext";
import "./AdvahiniHome.css";

const features = [
  {
    title: "Control every screen centrally",
    text: "Manage screens, content, playlists, schedules, overlays, and workspace access from one operational dashboard.",
  },
  {
    title: "Build with drag-drop widgets",
    text: "Place text widgets, image widgets, media, clocks, weather, announcements, and apps anywhere your screen design needs them.",
  },
  {
    title: "Manage scheduled content with precision",
    text: "Plan daypart playlists, recurring campaigns, date-based promotions, and urgent overrides so every screen shows the right content at the right time.",
  },
];

const capabilities = [
  "Cloud digital signage",
  "Remote content scheduling",
  "Remote screen management software",
  "Unlimited playlists",
  "Screen zones",
  "Overlays",
  "Portrait screens",
  "Android digital signage player",
  "Windows digital signage software",
  "Drag-drop text and image widgets",
  "Wide variety of apps",
];

const DEMO_VIDEO_URL = "https://www.loom.com/share/5f63423f2e80476f9aa20975440f17e7";
const DEMO_VIDEO_EMBED_URL = "https://www.loom.com/embed/5f63423f2e80476f9aa20975440f17e7";
const COOKIE_CONSENT_KEY = "advahini:homepage-cookie-consent";

const revealUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0 },
};

const slideInContent = {
  hidden: (direction = 1) => ({ opacity: 0, x: direction * 34 }),
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.58, ease: "easeOut" },
  },
};

const staggerChildren = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
    },
  },
};

const screenOpsCard = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.52,
      ease: "easeOut",
      staggerChildren: 0.08,
      delayChildren: 0.08,
    },
  },
};

const screenOpsInner = {
  hidden: { opacity: 0, x: -24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.48, ease: "easeOut" },
  },
};

const screenOpsInnerReverse = {
  hidden: { opacity: 0, x: 24 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.48, ease: "easeOut" },
  },
};

const screenOpsViewportProps = {
  initial: "hidden",
  whileInView: "visible",
  viewport: { once: true, amount: 0.28 },
};

const showcaseTabs = [
  {
    id: "cafe",
    label: "Cafe",
    badge: "@menu",
    title: "Run menu boards and promos without slowing service.",
    description: "Update breakfast, lunch, combo offers, and daypart campaigns from one publishing flow.",
    updated: "Updated 12 mins ago",
    stageClass: "adv-home-stage-screen--cafe",
    image: "/images/showcase/cafe.jpg",
  },
  {
    id: "office",
    label: "Office",
    badge: "@townhall",
    title: "Keep internal communications visible across every office display.",
    description: "Share leadership updates, KPI boards, onboarding content, and workplace announcements in real time.",
    updated: "Updated 8 mins ago",
    stageClass: "adv-home-stage-screen--office",
    image: "/images/showcase/office.jpg",
  },
  {
    id: "gym",
    label: "Gym",
    badge: "@fitness",
    title: "Promote classes, trainers, and offers throughout the day.",
    description: "Schedule class timetables, motivational loops, and upsell campaigns across zones and screens.",
    updated: "Updated 18 mins ago",
    stageClass: "adv-home-stage-screen--gym",
    image: "/images/showcase/gym.jpg",
  },
  {
    id: "event",
    label: "Event",
    badge: "@campaign",
    title: "Keep every campaign live, polished, and on schedule.",
    description: "Built for digital signage content teams that need clarity, speed, and control.",
    updated: "Updated 30 mins ago",
    stageClass: "adv-home-stage-screen--event",
    image: "/images/showcase/events.jpg",
  },
];

const appWidgets = [
  {
    name: "Embed",
    detail: "Embed websites or apps and show live content on screens.",
    imageSrc: "/images/apps/embed_icon.png",
    tone: "cyan",
  },
  {
    name: "Video",
    detail: "Play videos on your screens for promos, tutorials, or motion content.",
    imageSrc: "/images/apps/video_icon.png",
    tone: "violet",
  },
  {
    name: "Image",
    detail: "Upload and display images for promotions, menus, or branding.",
    imageSrc: "/images/apps/image_icon.png",
    tone: "gold",
  },
  {
    name: "YouTube",
    detail: "Play videos, Shorts, playlists, or tutorials from YouTube.",
    imageSrc: "/images/apps/youtube_icon.png",
    tone: "red",
  },
  {
    name: "Quiz",
    detail: "Generate multiple-choice quizzes from your text and publish them on screen.",
    imageSrc: "/images/apps/quiz_game_icon.png",
    tone: "blue",
  },
  {
    name: "PDF",
    detail: "Upload and show PDFs such as menus, schedules, or notices.",
    imageSrc: "/images/apps/pdf_icon.png",
    tone: "rose",
  },
  {
    name: "Video Slideshow",
    detail: "Loop multiple videos in a clean slideshow.",
    imageSrc: "/images/apps/video_slideshow_icon.png",
    tone: "violet",
  },
  {
    name: "Image Slideshow",
    detail: "Create image slideshows from multiple images.",
    imageSrc: "/images/apps/image_slideshow_icon.png",
    tone: "gold",
  },
  {
    name: "New York Times",
    detail: "Keep audiences informed with automatically updating headlines.",
    iconLabel: "T",
    imageSrc: "/images/apps/nytimes_icon.jpeg",
    tone: "black",
  },
  {
    name: "The Guardian",
    detail: "Display latest headlines from The Guardian.",
    iconLabel: "G",
    imageSrc: "/images/apps/guardian_icon.png",
    tone: "navy",
  },
  {
    name: "ABC News",
    detail: "Show ABC News headlines with image-supported news layouts.",
    iconLabel: "ABC",
    imageSrc: "/images/apps/abc_news_icon.png",
    tone: "black",
  },
  {
    name: "Al Jazeera",
    detail: "Display Al Jazeera breaking news and world coverage.",
    iconLabel: "AJ",
    imageSrc: "/images/apps/aljazeera_icon.png",
    tone: "gold",
  },
  {
    name: "Bloomberg",
    detail: "Show Bloomberg business, markets, and finance headlines.",
    iconLabel: "B",
    imageSrc: "/images/apps/bloomberg_ixon.jpeg",
    tone: "blue",
  },
  {
    name: "Der Spiegel",
    detail: "Publish Der Spiegel International headlines to your screens.",
    iconLabel: "DS",
    imageSrc: "/images/apps/der_spiegel_icon.png",
    tone: "orange",
  },
  {
    name: "El Pais",
    detail: "Display El Pais Espana headlines for Spanish news coverage.",
    iconLabel: "EP",
    imageSrc: "/images/apps/el_pais_icon.png",
    tone: "slate",
  },
  {
    name: "ESPN",
    detail: "Show ESPN sports updates and headline feeds.",
    iconLabel: "ESPN",
    imageSrc: "/images/apps/espn_icon.png",
    tone: "red",
  },
  {
    name: "Euronews Live TV",
    detail: "Run Euronews live TV through a YouTube-rendered player app.",
    iconLabel: "EU",
    imageSrc: "/images/apps/euro_news_icon.png",
    tone: "blue",
  },
  {
    name: "Fox News",
    detail: "Display Fox News headlines in the news app layout.",
    iconLabel: "FOX",
    imageSrc: "/images/apps/fox_news_icon.png",
    tone: "navy",
  },
  {
    name: "France 24",
    detail: "Show France 24 headlines or live TV in playlist scenes.",
    iconLabel: "F24",
    imageSrc: "/images/apps/france_24_icon.png",
    tone: "cyan",
  },
  {
    name: "Sky News",
    detail: "Add Sky News headlines to information screens.",
    iconLabel: "Sky",
    imageSrc: "/images/apps/sky_news_icon.jpeg",
    tone: "red",
  },
  {
    name: "Sky Sports",
    detail: "Keep sports audiences updated with Sky Sports headlines.",
    iconLabel: "Sky",
    imageSrc: "/images/apps/sky_sports_icon.jpeg",
    tone: "blue",
  },
  {
    name: "WiFi Share QR",
    detail: "Create a scannable WiFi QR screen with configurable colors and text.",
    iconLabel: "WiFi",
    imageSrc: "/images/apps/wifi.png",
    tone: "cyan",
  },
  {
    name: "Google Reviews",
    detail: "Show location reviews and ratings from a configured Google place.",
    iconLabel: "GR",
    imageSrc: "/images/apps/google_reviews.png",
    tone: "gold",
    badge: "Soon",
  },
  {
    name: "Bible Verses",
    detail: "Share inspirational Bible verses across your displays.",
    imageSrc: "/images/apps/bible_icon.png",
    tone: "slate",
  },
  {
    name: "Gita Verses",
    detail: "Share daily Bhagavad Gita verses and uplifting spiritual messages.",
    imageSrc: "/images/apps/geeta_icon.png",
    tone: "gold",
  },
  {
    name: "Microsoft PowerPoint",
    detail: "Show presentations and keep updates effortless.",
    iconLabel: "P",
    tone: "orange",
    badge: "Via embed app",
  },
  {
    name: "Microsoft Excel",
    detail: "Display live spreadsheets that stay up to date.",
    iconLabel: "Xl",
    tone: "green",
    badge: "Via embed app",
  },
  {
    name: "Microsoft Word",
    detail: "Show documents directly on your displays.",
    iconLabel: "W",
    tone: "blue",
    badge: "Via embed app",
  },
  {
    name: "Google Slides",
    detail: "Share slides seamlessly across your screens.",
    iconLabel: "S",
    tone: "yellow",
    badge: "Via embed app",
  },
  {
    name: "Google Sheets",
    detail: "Show live Google Sheets for schedules, data, and reports.",
    iconLabel: "Sh",
    tone: "green",
    badge: "Via embed app",
  },
  {
    name: "Google Docs",
    detail: "Display Docs instantly for announcements and live info.",
    iconLabel: "D",
    tone: "blue",
    badge: "Via embed app",
  },
  {
    name: "Announcement",
    detail: "Post quick updates and important notices without friction.",
    iconLabel: "Aa",
    tone: "indigo",
  },
  {
    name: "Days Of The World",
    detail: "Celebrate daily traditions and stories from around the world.",
    imageSrc: "/images/apps/world-day.png",
    tone: "violet",
  },
  {
    name: "Word Of The Day",
    detail: "Show a fresh word, meaning, and learning prompt every day.",
    imageSrc: "/images/apps/word_of_day_icon.png",
    tone: "cyan",
  },
  {
    name: "Canva",
    detail: "Easily import designs from Canva and show them on your signage.",
    imageSrc: "/images/apps/canva_icon.jpg",
    tone: "teal",
  },
  {
    name: "Instagram",
    detail: "Showcase latest Instagram posts in real time.",
    imageSrc: "/images/social/instagram_icon.png",
    tone: "pink",
  },
  {
    name: "Facebook Feeds",
    detail: "Display connected Facebook account posts and keep your audience informed.",
    imageSrc: "/images/social/facebook_icon.png",
    tone: "blue",
  },
];

const playerPlatforms = [
  {
    name: "Android player",
    detail: "Run Advahini on Android TV boxes, panels, and signage devices for simple always-on screen playback.",
  },
  {
    name: "Browser player",
    detail: "Open the player in a managed browser for quick setup on kiosk displays, meeting-room screens, and shared devices.",
  },
  {
    name: "Raspberry Pi player",
    detail: "Use Raspberry Pi devices for compact, low-cost signage playback in reception areas, branches, kiosks, and office screens.",
  },
  {
    name: "Portrait and overlay modes",
    detail: "Run vertical screens, split zones, and layered overlays from the same account you use for normal landscape displays.",
  },
];

const testimonials = [
  {
    quote:
      "We switched from manual USB updates to centralized scheduling. Our stores now push promos and weather widgets in minutes.",
    author: "Priya Nair",
    role: "Retail Operations Lead",
    company: "CityCart",
  },
  {
    quote:
      "The workspace model keeps each branch independent while HQ still manages schedules, playlists, and emergency overlays.",
    author: "Rahul Verma",
    role: "Network Administrator",
    company: "BrightCare Hospitals",
  },
  {
    quote:
      "Portrait screens, PDF notices, YouTube explainers, and live RSS updates all run from one clean workflow.",
    author: "Shalini Das",
    role: "Communications Manager",
    company: "Northview Campus",
  },
];

const pricingCurrencyTabs = [
  { code: "INR", label: "INR", countryCode: "IN" },
  { code: "EUR", label: "EUR", countryCode: "DE" },
  { code: "AUD", label: "AUD", countryCode: "AU" },
  { code: "USD", label: "USD", countryCode: "US" },
  { code: "GBP", label: "GBP", countryCode: "GB" },
];

const visiblePricingFeatures = (features = []) =>
  features.filter((feature) => feature && !/white\s*label/i.test(feature));

const formatMarketingPrice = (amount, currency) => {
  if (Number(amount || 0) <= 0) return "Free";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(Number(amount || 0));
  } catch {
    return `${currency || "INR"} ${Number(amount || 0).toFixed(0)}`;
  }
};

const planPriceSuffix = (plan) => {
  if (Number(plan?.unitAmount ?? plan?.unit_amount ?? 0) <= 0) return "";
  return plan?.pricing_type === "flat" ? "/month" : "screen/month";
};

export default function AdvahiniHome({ onOpenSalesContact }) {
  const { user, logout } = useAuth();
  const [activeShowcase, setActiveShowcase] = useState("event");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [livePricingPlans, setLivePricingPlans] = useState([]);
  const [pricingError, setPricingError] = useState("");
  const [pricingCurrencies, setPricingCurrencies] = useState(pricingCurrencyTabs);
  const [selectedPricingCurrency, setSelectedPricingCurrency] = useState("INR");
  const [blogPosts, setBlogPosts] = useState([]);
  const [showCookieConsent, setShowCookieConsent] = useState(() => {
    try {
      return window.localStorage.getItem(COOKIE_CONSENT_KEY) !== "accepted";
    } catch {
      return true;
    }
  });

  const scrollTo = (id) => {
    const node = document.getElementById(id);
    node?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileMenuOpen(false);
  };

  const activeTab = showcaseTabs.find((item) => item.id === activeShowcase) || showcaseTabs[0];
  const activeShowcaseIndex = Math.max(
    0,
    showcaseTabs.findIndex((item) => item.id === activeShowcase)
  );
  const changeShowcaseSlide = (direction) => {
    setActiveShowcase((current) => {
      const currentIndex = showcaseTabs.findIndex((item) => item.id === current);
      const nextIndex = (Math.max(currentIndex, 0) + direction + showcaseTabs.length) % showcaseTabs.length;
      return showcaseTabs[nextIndex].id;
    });
  };

  useEffect(() => {
    const timer = window.setInterval(() => {
      changeShowcaseSlide(1);
    }, 4200);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;

    const loadPricing = async (nextCurrency = "INR") => {
      try {
        const selectedTab =
          pricingCurrencyTabs.find((item) => item.code === nextCurrency) || pricingCurrencyTabs[0];
        const response = await getUpgradePlans(
          {
            country_code: selectedTab.countryCode,
            currency_code: selectedTab.code,
          },
          {
            advahiniSkipGlobalLoading: true,
          }
        );
        const payload = response?.data || {};
        const provider = String(payload.payment_provider || "").toLowerCase();
        const rows = Array.isArray(payload.prices) ? payload.prices : [];
        const visibleCurrencies = pricingCurrencyTabs.filter((tab) =>
          Array.isArray(payload.currencies)
            ? payload.currencies.some(
                (currency) => String(currency.code || "").toUpperCase() === tab.code
              )
            : true
        );
        if (!active) return;
        setSelectedPricingCurrency(payload?.selected_currency?.code || selectedTab.code);
        setPricingCurrencies(visibleCurrencies.length ? visibleCurrencies : pricingCurrencyTabs);
        if (!rows.length) {
          setLivePricingPlans([]);
          setPricingError("Pricing is not configured yet. Please contact sales.");
          return;
        }
        setPricingError("");
        setLivePricingPlans(
          rows
            .filter((row) => {
              const key = String(row.price_key || "").toLowerCase();
              return key === "trial" || key === "business";
            })
            .map((row, index) => ({
            price_key: row.price_key,
            name: row.name || "Plan",
            description: row.description || "Flexible digital signage subscription pricing.",
            unitAmount: Number(row.unit_amount || 0),
            price: formatMarketingPrice(row.unit_amount, row.currency || payload?.selected_currency?.code || "INR"),
            accent: row.marketing_accent || (index === 0 ? "warm" : index === rows.length - 1 ? "dark" : ""),
            recommended: Boolean(row.marketing_badge) && /recommended/i.test(row.marketing_badge),
            badge: row.marketing_badge || "",
            features:
               Array.isArray(row.marketing_features) && row.marketing_features.length
                 ? visiblePricingFeatures(row.marketing_features)
                 : [],
            ctaLabel: row.marketing_cta_label || `Choose ${row.name || "Plan"}`,
            priceSuffix: planPriceSuffix(row),
            marketingImageUrl: row.marketing_image_url || "",
            checkoutEnabled: row.checkout_enabled !== false,
            providerLabel:
              ["INR", "USD", "GBP", "AUD", "EUR"].includes(String(row.currency || payload?.selected_currency?.code || selectedTab.code || "").toUpperCase())
                ? "Paid via Razorpay Checkout"
                : provider === "razorpay" || row.provider_scope === "razorpay"
                ? "Paid via Razorpay Checkout"
                : provider === "paddle" || row.provider_scope === "paddle"
                    ? "Paid via Paddle Checkout"
                  : "",
          }))
        );
      } catch (err) {
        if (!active) return;
        setLivePricingPlans([]);
        setPricingError(err?.response?.data?.detail || "Pricing is not available right now. Please contact sales.");
      }
    };

    loadPricing(selectedPricingCurrency);
    return () => {
      active = false;
    };
  }, [selectedPricingCurrency]);

  useEffect(() => {
    let active = true;
    const loadBlogPosts = async () => {
      try {
        const response = await getPublicBlogPosts(
          { limit: 3 },
          { advahiniSkipGlobalLoading: true }
        );
        if (active) setBlogPosts(response.data || []);
      } catch {
        if (active) setBlogPosts([]);
      }
    };
    loadBlogPosts();
    return () => {
      active = false;
    };
  }, []);

  const handlePricingCurrencyChange = (currencyCode) => {
    setSelectedPricingCurrency(currencyCode);
  };

  const closeCookieConsent = () => {
    try {
      window.localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    } catch {}
    setShowCookieConsent(false);
  };

  return (
    <div className="adv-home">
      <SEO
        title="Advahini | Cloud Digital Signage Software for Android & Windows"
        description="Advahini is a cloud-based digital signage platform that helps businesses manage screens remotely, schedule content, create playlists, and update displays instantly across Android and Windows devices."
        path="/"
        keywords="digital signage software, cloud digital signage, digital signage platform, Android digital signage player, Windows digital signage software, remote screen management software, retail digital signage software, menu board software, enterprise digital signage"
      />
      <header className={`adv-home-header${mobileMenuOpen ? " adv-home-header--menu-open" : ""}`}>
        <div className="adv-home-shell adv-home-header-shell">
          <Link to="/" className="adv-home-brand">
            <span className="adv-home-brand-mark" aria-hidden="true">
              A
            </span>
            <span className="adv-home-brand-text">AdVahini</span>
          </Link>

          <button
            type="button"
            className="adv-home-menu-toggle"
            aria-label={mobileMenuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className="adv-home-nav" aria-label="Primary">
            <button type="button" onClick={() => scrollTo("screen-operations")}>
              Screens
            </button>
            <button type="button" onClick={() => scrollTo("playlist-builder")}>
              Playlists
            </button>
            <button type="button" onClick={() => scrollTo("players")}>
              Players
            </button>
            <button type="button" onClick={() => scrollTo("pricing")}>
              Pricing
            </button>
            <a
              href="https://calendar.app.google/qJYokhfhKR5d5hWA6"
              target="_blank"
              rel="noopener noreferrer"
              title="Book a demo"
              onClick={() => setMobileMenuOpen(false)}
            >
              Book a demo
            </a>
          </nav>

          <div className="adv-home-header-actions">
            {user ? (
              <PublicAccountMenu
                user={user}
                logout={logout}
                onNavigate={() => setMobileMenuOpen(false)}
              />
            ) : (
              <>
                <Link to="/login" className="adv-home-signin-link" onClick={() => setMobileMenuOpen(false)}>
                  Sign In
                </Link>
                <Link to="/register" className="adv-home-dark-btn" onClick={() => setMobileMenuOpen(false)}>
                  Try For Free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="adv-home-hero">
          <div className="adv-home-shell adv-home-hero-shell">
            <div className="adv-home-hero-copy">
              <span className="adv-home-eyebrow">Digital signage software</span>
              <h1>Digital signage software that keeps screen management simple.</h1>
              <p>
                Advahini is a cloud digital signage platform for Android digital signage
                players, Windows digital signage software, remote screen management, retail
                digital signage, menu board software, and enterprise digital signage rollouts.
              </p>
              {!user ? (
                <div className="adv-home-hero-actions">
                  <Link to="/register" className="adv-home-dark-btn adv-home-dark-btn--large">
                    Start Free Trial
                  </Link>
                  <button
                    type="button"
                    className="adv-home-light-btn adv-home-light-btn--button adv-home-sales-btn"
                    onClick={() => onOpenSalesContact?.()}
                  >
                    Contact Sales
                  </button>
                </div>
              ) : (
                <div className="adv-home-hero-actions">
                  <button
                    type="button"
                    className="adv-home-light-btn adv-home-light-btn--button adv-home-sales-btn"
                    onClick={() => onOpenSalesContact?.()}
                  >
                    Contact Sales
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        <section id="showcase" className="adv-home-showcase">
          <div className="adv-home-shell">
            <div className="adv-home-stage">
              {showcaseTabs.map((tab) => (
                <div
                  key={`backdrop-${tab.id}`}
                  className={`adv-home-stage-backdrop${activeTab.id === tab.id ? " active" : ""}`}
                  style={{ "--adv-showcase-bg": `url(${tab.image})` }}
                  aria-hidden="true"
                />
              ))}
              <div className="adv-home-stage-wall adv-home-stage-wall--left" />
              <div className="adv-home-stage-wall adv-home-stage-wall--right" />
              {showcaseTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`adv-home-stage-screen ${tab.stageClass}${activeTab.id === tab.id ? " active" : ""}`}
                  style={{ "--adv-showcase-bg": `url(${tab.image})` }}
                >
                  <div className="adv-home-screen-content">
                    <div className="adv-home-screen-media">
                      <div className="adv-home-media-badge">{tab.badge}</div>
                      <div className="adv-home-media-figure" />
                    </div>
                    <div className="adv-home-screen-message">
                      <h2>{tab.title}</h2>
                      <p>{tab.description}</p>
                      <span>{tab.updated}</span>
                    </div>
                  </div>
                </div>
              ))}
              <div className="adv-home-stage-tabs" role="tablist" aria-label="Showcase categories">
                {showcaseTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab.id === tab.id}
                    className={activeTab.id === tab.id ? "active" : ""}
                    onClick={() => setActiveShowcase(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="adv-home-stage-progress" aria-hidden="true">
                {showcaseTabs.map((tab, index) => (
                  <span
                    key={`progress-${tab.id}`}
                    className={index === activeShowcaseIndex ? "active" : ""}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <motion.section
          id="features"
          className="adv-home-platform"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.18 }}
        >
          <div className="adv-home-shell adv-home-platform-shell">
            <motion.div className="adv-home-platform-copy" variants={revealUp}>
              <span className="adv-home-eyebrow">One operating layer</span>
              <h2>Manage all your digital screens from one place.</h2>
              <p>
                From onboarding displays to publishing campaigns, AdVahini gives teams one
                cleaner workflow for cloud digital signage, remote screen management software,
                retail campaigns, restaurant menu boards, and enterprise digital signage.
              </p>
              <div className="adv-home-capability-list">
                {capabilities.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </motion.div>

            <div className="adv-home-feature-grid">
              {features.map((feature, index) => (
                <motion.article
                  key={feature.title}
                  className="adv-home-feature-card"
                  custom={index % 2 === 0 ? -1 : 1}
                  variants={slideInContent}
                >
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          id="demo-video"
          className="adv-home-demo"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="adv-home-shell adv-home-demo-shell">
            <div className="adv-home-demo-copy">
              <span className="adv-home-eyebrow">Product demo</span>
              <h2>See how quickly a screen becomes a live Advahini display.</h2>
              <p>
                Watch a short walkthrough of playlists, zones, schedules, overlays, and
                screen publishing in the Advahini product demo.
              </p>
              <div className="adv-home-demo-actions">
                <span>See how easy it is to connect:</span>
                <a href={DEMO_VIDEO_URL} target="_blank" rel="noreferrer" className="adv-home-watch-btn">
                  <FaPlay />
                  Watch the video
                </a>
              </div>
            </div>
            <div className="adv-home-demo-media">
              <iframe
                src={DEMO_VIDEO_EMBED_URL}
                title="Advahini product demo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </div>
        </motion.section>

        <section id="screen-operations" className="adv-home-control">
          <div className="adv-home-shell">
            <div className="adv-home-section-head">
              <span className="adv-home-eyebrow">Screen operations</span>
              <h2>Design every screen with zones, overlays, and widgets.</h2>
              <p>
                Drag text, image, media, weather, clock, announcement, and app widgets
                into the right place for every screen.
              </p>
            </div>
            <div className="adv-home-control-grid">
              <motion.article className="adv-home-show-card adv-home-show-card--apps" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInner}>
                  <h3>Loads Of Apps</h3>
                  <p>Pick news, social feeds, WiFi QR, reviews, YouTube, Canva, documents, and media from one app library.</p>
                </motion.div>
                <motion.div className="adv-home-app-picker-preview" variants={staggerChildren} aria-hidden="true">
                  <motion.div className="adv-home-app-picker-search" variants={screenOpsInnerReverse} />
                  <div className="adv-home-app-picker-list">
                    <motion.div className="featured" variants={screenOpsInner}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/france_24_icon.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>France 24 Live</strong>
                      <small>News</small>
                    </motion.div>
                    <motion.div variants={screenOpsInnerReverse}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/aljazeera_icon.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>Al Jazeera</strong>
                      <small>News</small>
                    </motion.div>
                    <motion.div variants={screenOpsInner}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/wifi.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>WiFi Share QR</strong>
                      <small>Utility</small>
                    </motion.div>
                    <motion.div variants={screenOpsInnerReverse}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/google_reviews.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>Google Reviews</strong>
                      <small>Reviews - Soon</small>
                    </motion.div>
                    <motion.div variants={screenOpsInner}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/jewellery_rates.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>Jewellery Rates</strong>
                      <small>Retail</small>
                    </motion.div>
                    <motion.div variants={screenOpsInnerReverse}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/exchange_rates.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>Currency Rates</strong>
                      <small>Finance</small>
                    </motion.div>
                    <motion.div variants={screenOpsInner}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/air_quality.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>Air Quality</strong>
                      <small>Utility</small>
                    </motion.div>
                    <motion.div variants={screenOpsInnerReverse}>
                      <span className="app-dot app-dot--image">
                        <img src="/images/apps/espn_icon.png" alt="" aria-hidden="true" />
                      </span>
                      <strong>ESPN</strong>
                      <small>Sports</small>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--playlists" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInnerReverse}>
                  <h3>Unlimited Playlists</h3>
                  <p>Build content playlists for every branch, campaign, zone, and audience.</p>
                </motion.div>
                <motion.div className="adv-home-playlist-preview" variants={staggerChildren} aria-hidden="true">
                  {[
                    ["1", "World news", "New York Times", "NW"],
                    ["2", "New team member", "Profile", "TM"],
                    ["3", "Image", "Image", "IM"],
                    ["4", "New years eve", "Canva", "NY"],
                    ["5", "Team lunch", "Canva", "TL"],
                  ].map(([order, title, source, badge]) => (
                    <motion.div key={title} className="adv-home-playlist-preview-row" variants={screenOpsInnerReverse}>
                      <span>{order}</span>
                      <div>{badge}</div>
                      <strong>{title}</strong>
                      <small>{source}</small>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--schedules" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInner}>
                  <h3>Schedules</h3>
                  <p>Schedule each screen to show the right content at the right time.</p>
                </motion.div>
                <motion.div className="adv-home-calendar-preview" variants={staggerChildren} aria-hidden="true">
                  <motion.div className="adv-home-calendar-head" variants={screenOpsInnerReverse}>
                    <span>Monday</span>
                    <span>Tuesday</span>
                    <span>Wednesday</span>
                  </motion.div>
                  <motion.div className="adv-home-calendar-body" variants={screenOpsInner}>
                    <span>09:00</span>
                    <strong className="slot slot-a">Meeting<br />09:00 - 12:00</strong>
                    <strong className="slot slot-b">Morning<br />08:00 - 12:00</strong>
                    <span>12:00</span>
                    <strong className="slot slot-c">Team Breakfast<br />09:00 - 10:00</strong>
                    <span>18:00</span>
                  </motion.div>
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--screens" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInnerReverse}>
                  <h3>Screens</h3>
                  <p>Add as many screens as you like and organize them by client, branch, or zone.</p>
                </motion.div>
                <motion.div className="adv-home-screen-stack-preview" variants={staggerChildren} aria-hidden="true">
                  <motion.img src="/images/showcase/gym.jpg" alt="" variants={screenOpsInner} />
                  <motion.img src="/images/showcase/events.jpg" alt="" variants={screenOpsInnerReverse} />
                  <motion.img src="/images/showcase/cafe.jpg" alt="" variants={screenOpsInner} />
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--zones" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInner}>
                  <h3>Zones</h3>
                  <p>Split a screen into playlist, sidebar, ticker, offer, and widget areas.</p>
                </motion.div>
                <motion.div className="adv-home-zones-layout-preview" variants={staggerChildren} aria-hidden="true">
                  <img src="/images/showcase/zonesgym.jpg" alt="" />
                  <motion.div className="adv-home-zones-main" variants={screenOpsInner}>
                    <strong>Beginner Class</strong>
                    <span>Main playlist</span>
                  </motion.div>
                  <motion.div className="adv-home-zones-side" variants={screenOpsInnerReverse}>
                    <strong>Refer a friend</strong>
                    <span>Side zone</span>
                  </motion.div>
                  <motion.div className="adv-home-zones-picker" variants={screenOpsInnerReverse}>
                    <span>Zone 3</span>
                    <small>Image library</small>
                    <small>Clock widget</small>
                    <small>Text widget</small>
                  </motion.div>
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--overlays" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInnerReverse}>
                  <h3>Overlays</h3>
                  <p>Add clocks, tickers, notices, QR panels, weather, and media over live content.</p>
                </motion.div>
                <motion.div className="adv-home-overlay-preview" variants={staggerChildren} aria-hidden="true">
                  <img src="/images/showcase/cafe.jpg" alt="" />
                  <motion.div className="adv-home-overlay-clock" variants={screenOpsInner}>10:43 am</motion.div>
                  <motion.div className="adv-home-overlay-panel" variants={screenOpsInnerReverse}>
                    <strong>Time</strong>
                    <span>Style</span>
                    <small>White</small>
                    <span>Format</span>
                    <small>12 hour</small>
                  </motion.div>
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--portrait" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInner}>
                  <h3>Portrait Screens</h3>
                  <p>Publish clean portrait layouts for lobbies, menus, gyms, campuses, and kiosks.</p>
                </motion.div>
                <motion.div className="adv-home-portrait-preview" variants={screenOpsInnerReverse} aria-hidden="true">
                  <div className="adv-home-portrait-device">
                    <img src="/images/showcase/gym.jpg" alt="" />
                    <span>Gym Club</span>
                    <strong>Pilates<br />10:30am<br />Daily</strong>
                  </div>
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--widgets" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInnerReverse}>
                  <h3>Drag & Drop Widgets</h3>
                  <p>Add text, image, media, clock, weather, and announcement widgets anywhere.</p>
                </motion.div>
                <motion.div className="adv-home-widget-list-preview" variants={staggerChildren} aria-hidden="true">
                  {[
                    ["C", "Clock"],
                    ["M", "Media"],
                    ["W", "Weather"],
                    ["A", "Announcement"],
                    ["T", "Text"],
                    ["I", "Image"],
                  ].map(([badge, label]) => (
                    <motion.div key={label} variants={screenOpsInner}>
                      <span>{badge}</span>
                      <strong>{label}</strong>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.article>

              <motion.article className="adv-home-show-card adv-home-show-card--workspaces" variants={screenOpsCard} {...screenOpsViewportProps}>
                <motion.div className="adv-home-show-card-copy" variants={screenOpsInner}>
                  <h3>Workspace Control</h3>
                  <p>Give each branch or client its own screens, playlists, users, and approvals while you keep central visibility.</p>
                </motion.div>
                <motion.div className="adv-home-workspace-table-preview" variants={staggerChildren} aria-hidden="true">
                  <motion.div className="adv-home-workspace-table-head" variants={screenOpsInnerReverse}>
                    <span>Workspace</span>
                    <span>Screens</span>
                    <span>Playlists</span>
                  </motion.div>
                  {[
                    ["Dublin Office", "20", "4"],
                    ["Retail North", "8", "2"],
                    ["Airport Lounge", "15", "5"],
                    ["Client Demo", "4", "1"],
                  ].map(([workspace, screens, playlists]) => (
                    <motion.div key={workspace} className="adv-home-workspace-table-row" variants={screenOpsInner}>
                      <div className="adv-home-workspace-table-name">
                        <span>{workspace.slice(0, 1)}</span>
                        <strong>{workspace}</strong>
                      </div>
                      <small className="adv-home-workspace-table-count">{screens}</small>
                      <small className="adv-home-workspace-table-count">{playlists}</small>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.article>
            </div>
          </div>
        </section>

        <motion.section
          id="playlist-builder"
          className="adv-home-builder"
          variants={staggerChildren}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.14 }}
        >
          <div className="adv-home-shell adv-home-builder-shell">
            <motion.div className="adv-home-section-head" variants={revealUp}>
              <span className="adv-home-eyebrow">Apps and widgets</span>
              <h2>Loads of Apps</h2>
              <p>
                Social media walls, news, Canva, videos, dashboards, documents, announcements,
                and live widgets for every screen.
              </p>
            </motion.div>

            <div className="adv-home-app-catalog">
              {appWidgets.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.article
                    key={item.name}
                    className="adv-home-app-widget-card"
                    custom={index % 2 === 0 ? -1 : 1}
                    variants={slideInContent}
                  >
                    <div className={`adv-home-app-widget-icon adv-home-app-widget-icon--${item.tone}${item.imageSrc ? " adv-home-app-widget-icon--image" : ""}`}>
                      {item.imageSrc ? <img src={item.imageSrc} alt="" aria-hidden="true" /> : Icon ? <Icon /> : <span>{item.iconLabel}</span>}
                    </div>
                    <div className="adv-home-app-widget-copy">
                      <div className="adv-home-app-widget-title-row">
                        <h3>{item.name}</h3>
                        {item.badge ? <span>{item.badge}</span> : null}
                      </div>
                      <p>{item.detail}</p>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </motion.section>

        <motion.section
          id="players"
          className="adv-home-player-support"
          variants={revealUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="adv-home-shell">
            <div className="adv-home-section-head">
              <span className="adv-home-eyebrow">Player support</span>
              <h2>Use your hardware</h2>
              <p>
                Bring your Android screen, browser, Raspberry Pi, or kiosk display. Advahini connects
                the screen, keeps playback full screen, and gives your team one place to
                update content remotely.
              </p>
            </div>

            <div className="adv-home-player-grid">
              {playerPlatforms.map((platform) => (
                <motion.article
                  key={platform.name}
                  className="adv-home-player-card"
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.2 }}
                >
                  <h3>{platform.name}</h3>
                  <p>{platform.detail}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </motion.section>

        <section id="testimonials" className="adv-home-testimonials">
          <div className="adv-home-shell">
            <div className="adv-home-section-head">
              <span className="adv-home-eyebrow">Testimonials</span>
              <h2>Trusted by teams running live digital signage every day.</h2>
            </div>

            <div className="adv-home-testimonial-grid">
              {testimonials.map((item) => (
                <article key={`${item.author}-${item.company}`} className="adv-home-testimonial-card">
                  <div className="adv-home-testimonial-rating">Rated 5.0 by live deployments</div>
                  <p>"{item.quote}"</p>
                  <strong>{item.author}</strong>
                  <span>
                    {item.role}, {item.company}
                  </span>
                </article>
              ))}
            </div>
          </div>
        </section>

        {blogPosts.length ? (
          <section id="blog" className="adv-home-blog">
            <div className="adv-home-shell">
              <div className="adv-home-section-head adv-home-section-head--left">
                <span className="adv-home-eyebrow">Advahini Blog</span>
                <h2>Notes on content, screens, and smarter signage operations.</h2>
                <p>Read practical updates from the team building Advahini.</p>
              </div>
              <div className="adv-home-blog-grid">
                {blogPosts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="adv-home-blog-card">
                    <span>{post.is_featured ? "Featured" : post.author_name || "Advahini"}</span>
                    <h3>{post.title}</h3>
                    <p>{post.excerpt || "Read the latest Advahini update."}</p>
                  </Link>
                ))}
              </div>
              <Link to="/blog" className="adv-home-blog-link">Read all posts</Link>
            </div>
          </section>
        ) : null}

        <section id="pricing" className="adv-home-cta">
          <div className="adv-home-shell">
            <div className="adv-home-pricing">
              <div className="adv-home-pricing-head">
                <span className="adv-home-eyebrow">Pricing</span>
                <h2>Simple pricing for every stage of your signage rollout.</h2>
                <p>
                  Start small, scale across more screens, and move into advanced control
                  only when your operation needs it.
                </p>
                <div className="adv-home-pricing-tabs" role="tablist" aria-label="Pricing currency">
                  {pricingCurrencies.map((currency) => (
                    <button
                      key={currency.code}
                      type="button"
                      role="tab"
                      aria-selected={currency.code === selectedPricingCurrency}
                      className={currency.code === selectedPricingCurrency ? "active" : ""}
                      onClick={() => handlePricingCurrencyChange(currency.code)}
                    >
                      {currency.label || currency.code}
                    </button>
                  ))}
                </div>
              </div>

              <div className="adv-home-pricing-grid">
                {pricingError ? (
                  <div className="adv-home-pricing-empty" role="alert">
                    {pricingError}
                  </div>
                ) : null}
                {!pricingError && livePricingPlans.map((plan) => (
                  <article
                    key={plan.name}
                    className={`adv-home-price-card${
                      plan.recommended ? " adv-home-price-card--recommended" : ""
                    }${plan.accent === "warm" ? " adv-home-price-card--warm" : ""}${
                      plan.accent === "dark" ? " adv-home-price-card--dark" : ""
                    }${plan.checkoutEnabled === false ? " adv-home-price-card--coming-soon" : ""}`}
                  >
                    {plan.badge ? (
                      <span className="adv-home-price-badge">{plan.badge}</span>
                    ) : null}
                    <h3>{plan.name}</h3>
                    <p>{plan.description}</p>
                    {plan.marketingImageUrl ? (
                      <div className="adv-home-price-media">
                        <img src={plan.marketingImageUrl} alt={`${plan.name} payment and subscription`} />
                      </div>
                    ) : null}
                    <ul>
                      {plan.features.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                    <div className="adv-home-price-row">
                      <strong>{plan.price}</strong>
                      {plan.priceSuffix ? <span>{plan.priceSuffix}</span> : null}
                    </div>
                    {plan.providerLabel ? (
                      <div className="adv-home-price-provider">{plan.providerLabel}</div>
                    ) : null}
                    {!user && plan.checkoutEnabled !== false ? (
                      <Link to="/register" className="adv-home-light-btn">
                        {plan.ctaLabel || `Choose ${plan.name}`}
                      </Link>
                    ) : null}
                    {!user && plan.checkoutEnabled === false ? (
                      <button type="button" className="adv-home-light-btn adv-home-light-btn--button" disabled>
                        {plan.ctaLabel || "Coming Soon"}
                      </button>
                    ) : null}
                  </article>
                ))}
              </div>
            </div>

          </div>
        </section>
      </main>
      {showCookieConsent ? (
        <div className="adv-home-cookie-banner" role="region" aria-label="Cookie notice">
          <div className="adv-home-cookie-copy">
            <strong>Cookies on Advahini</strong>
            <p>
              We use cookies and local storage to keep the site working, remember preferences, and improve the product experience.
            </p>
          </div>
          <div className="adv-home-cookie-actions">
            <button type="button" className="adv-home-cookie-accept" onClick={closeCookieConsent}>
              Accept
            </button>
            <button type="button" className="adv-home-cookie-close" onClick={closeCookieConsent} aria-label="Close cookie notice">
              <FaTimes aria-hidden="true" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
